import { type FormikProps } from 'formik';

import { Card } from '@hivebrite/design-system/Card';

import {
  type AcceptSuggestionAction,
  type FieldSuggestion,
  type ParentFormikFormValues,
  type RejectSuggestionAction,
  type UpdateSuggestionAction,
} from 'hb-react/shared/components/AIFormWidget/types';

import {
  AcceptedState,
  PendingState,
  RejectedState,
} from './components/SuggestionState';
import { useSuggestion } from './hooks';

interface SuggestionProps {
  acceptSuggestion: AcceptSuggestionAction;
  formik: FormikProps<ParentFormikFormValues>;
  rejectSuggestion: RejectSuggestionAction;
  suggestion: FieldSuggestion;
  updateSuggestion: UpdateSuggestionAction;
}

export const Suggestion = ({
  suggestion,
  acceptSuggestion,
  rejectSuggestion,
  updateSuggestion,
  formik,
}: SuggestionProps) => {
  const {
    suggestion: suggestionWithHistory,
    navigateHistory,
    handleUndo,
    handleUndoRejection,
  } = useSuggestion({
    suggestion,
    updateSuggestion,
    formik,
  });

  return (
    <Card key={suggestion.id} p={4}>
      <SuggestionContent
        suggestion={suggestion}
        acceptSuggestion={acceptSuggestion}
        rejectSuggestion={rejectSuggestion}
        updateSuggestion={updateSuggestion}
        suggestionWithHistory={suggestionWithHistory}
        navigateHistory={navigateHistory}
        handleUndo={handleUndo}
        handleUndoRejection={handleUndoRejection}
      />
    </Card>
  );
};

const SuggestionContent = ({
  suggestion,
  acceptSuggestion,
  rejectSuggestion,
  updateSuggestion,
  suggestionWithHistory,
  navigateHistory,
  handleUndo,
  handleUndoRejection,
}) => {
  switch (suggestion.status) {
    case 'accepted':
      return <AcceptedState suggestion={suggestion} onUndo={handleUndo} />;
    case 'rejected':
      return (
        <RejectedState suggestion={suggestion} onUndo={handleUndoRejection} />
      );
    default:
      return (
        <PendingState
          suggestion={suggestion}
          suggestionWithHistory={suggestionWithHistory}
          acceptSuggestion={acceptSuggestion}
          rejectSuggestion={rejectSuggestion}
          updateSuggestion={updateSuggestion}
          navigateHistory={navigateHistory}
        />
      );
  }
};
